import React from 'react';
import { Input } from 'modules/styled/web';
import { Row, View, BodyText, Button, WarningBox, ErrorBox } from 'modules/styled';
import { useRelParam, useSRelParam } from 'web3/hooks';
import { ActivityIndicator } from 'react-native-web';
import { gql } from 'graphql-tag';
import { useMutation, useQuery } from '@apollo/react-hooks';

const SEND_VERIFY_EMAIL = gql`
  mutation sendEmail {
    sendEmail(action: "token claim")
  }
`;

const GET_VEST = gql`
  query {
    getVest {
      longVest
      shortVest
      nonce
    }
  }
`;

const SEND_CLAIM_VEST = gql`
  mutation claimVest($token: String!) {
    claimVest(token: $token) {
      longVestBN
      shortVestBN
      signature
      earning
      nonce
    }
  }
`;

export const useClaimVest = () => {
  const [
    claimUnvestedTokens,
    { data: { claimVest = undefined } = {}, loading: claimingVest, error: claimError },
  ] = useMutation(SEND_CLAIM_VEST);
  return { claimUnvestedTokens, claimVest, claimingVest, claimError };
};

export const useVerifyEmail = () => {
  const [
    verifyEmail,
    { data: { sendEmail = undefined } = {}, loading: sendingEmail, error: emailError },
  ] = useMutation(SEND_VERIFY_EMAIL);
  return { sendEmail, verifyEmail, sendingEmail, emailError };
};

export interface ClaimProps {
  canClaim: number;
  close: () => void;
  type?: string;
}

interface VerifyEmailProps {
  cashOut: any;
  setToken: any;
  token: string;
  email: string;
}

export function VerifyEmail({ cashOut, setToken, token, email }) {
  return (
    <View>
      <BodyText sx={{ mb: 3 }}>
        We emaild a verification code to {email}.{'\n'}Please paste the code below to
        complete the transaction.
      </BodyText>
      <Row>
        <Input
          sx={{
            mt: 0,
            flex: 1,
            mr: 1,
            height: 8,
            py: 1,
            px: 1,
            fontWeight: 'bold',
          }}
          onChange={({ target: { value } }) => setToken(value)}
          value={token}
        />
        <Button
          disabled={!token || token === ''}
          sx={{
            height: 8,
            mr: 'auto',
          }}
          onPress={cashOut}
        >
          Verify Email
        </Button>
      </Row>
    </View>
  );
}

export const renderWarnings = (showUnclamedWarning, emailError) => {
  if (showUnclamedWarning)
    return (
      <WarningBox sx={{ mb: 2 }}>
        <BodyText>
          Warning: You have not completed a previous transfer attempt, press the
          'Transfer' button below to complete.
        </BodyText>
      </WarningBox>
    );

  if (emailError)
    return (
      <ErrorBox sx={{ mb: 2 }}>
        <BodyText>{emailError.message}</BodyText>
      </ErrorBox>
    );

  return (
    <WarningBox sx={{ mb: 2 }}>
      <BodyText>
        Warning: You will have to pay for Ethereum gas when you claim REL tokens.
        Depending on network congestion this can cost up to $30 or more.
      </BodyText>
      <BodyText sx={{ mt: 1 }}>
        For this reason we don't reccomend claiming less than 20 REL.
      </BodyText>
    </WarningBox>
  );
};

export const useUnclaimedVest = (account?: string) => {
  const { res: userNonce } = useSRelParam('nonceOf', [account]);
  const nonce = userNonce != null ? parseInt(userNonce.toNumber()) : undefined;
  const { data: { getVest: vest } = {} } = useQuery(GET_VEST);
  if (nonce != null && vest?.nonce !== nonce) return null;
  return vest;
};

export const useUnclaimedSig = (user: any, account?: string) => {
  const { res: userNonce } = useRelParam('nonceOf', [account]);

  const nonce = userNonce ? parseInt(userNonce.toNumber()) : null;
  const unclaimedSig =
    user &&
    user.cashOut &&
    nonce != null &&
    user.cashOut.nonce >= Number(nonce) &&
    user.cashOut;

  return unclaimedSig;
};

export enum ProgressType {
  EMAIL = 'EMAIL',
  TX = 'TX',
}

export const Progress = ({ type }: { type: ProgressType }) => {
  return (
    <View
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'modalBackground',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <ActivityIndicator />
      <BodyText sx={{ mt: 2 }}>
        {type == ProgressType.EMAIL
          ? 'Sending Email Verification Code'
          : 'Processing Transaction'}
      </BodyText>
    </View>
  );
};
